import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Divider,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  FormGroup,
  Grid,
  Alert
} from '@mui/material';

const Settings = () => {
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Box sx={{ mb: { xs: 2.5, md: 3.5 } }}>
        <Typography variant="h3" component="h1" sx={{ fontSize: { xs: '1.45rem', md: '1.75rem' } }}>
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Company details, notifications, and integrations
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        This page is a preview — the values shown are placeholders and are not
        saved anywhere yet. Saving requires a settings endpoint on the backend,
        so the buttons below are disabled until that exists.
      </Alert>

      <Paper sx={{ mb: 3, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="General" />
          <Tab label="Notifications" />
          <Tab label="API Integration" />
          <Tab label="User Management" />
        </Tabs>
        
        <Box p={3}>
          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="500">
                General Settings
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    defaultValue="Skyline Construction & Homes"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Email Address"
                    defaultValue="info@skylineroofing.com"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    defaultValue="(555) 123-4567"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Website URL"
                    defaultValue="https://skylineroofing.com"
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Address"
                    defaultValue="1234 Main Street"
                    margin="normal"
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="City"
                      defaultValue="Richmond"
                      margin="normal"
                    />
                    <TextField
                      fullWidth
                      label="State"
                      defaultValue="VA"
                      margin="normal"
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="Zip Code"
                    defaultValue="23219"
                    margin="normal"
                  />
                  <FormGroup sx={{ mt: 2 }}>
                    <FormControlLabel 
                      control={<Switch defaultChecked />} 
                      label="Enable dark mode" 
                    />
                    <FormControlLabel 
                      control={<Switch defaultChecked />} 
                      label="Show analytics on dashboard" 
                    />
                  </FormGroup>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="500">
                Notification Settings
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Typography sx={{ mb: 2 }}>
                Configure when and how you receive notifications.
              </Typography>
              
              <FormGroup>
                <FormControlLabel 
                  control={<Switch defaultChecked />} 
                  label="Email notifications for new customers" 
                />
                <FormControlLabel 
                  control={<Switch defaultChecked />} 
                  label="Email notifications for new appointments" 
                />
                <FormControlLabel 
                  control={<Switch />} 
                  label="SMS notifications for new customers" 
                />
                <FormControlLabel 
                  control={<Switch defaultChecked />} 
                  label="SMS notifications for new appointments" 
                />
                <FormControlLabel 
                  control={<Switch defaultChecked />} 
                  label="Daily summary email" 
                />
                <FormControlLabel 
                  control={<Switch />} 
                  label="Weekly report email" 
                />
              </FormGroup>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="500">
                  Notification Recipients
                </Typography>
                <TextField
                  fullWidth
                  label="Email Recipients (comma separated)"
                  defaultValue="admin@skylineroofing.com, manager@skylineroofing.com"
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="SMS Recipients (comma separated)"
                  defaultValue="+15551234567, +15559876543"
                  margin="normal"
                />
              </Box>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          )}
          
          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="500">
                API Integration Settings
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Typography sx={{ mb: 2 }}>
                Configure the integration with external systems like CRM and marketing tools.
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom fontWeight="500">
                Roofr CRM Integration (via Zapier)
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Zapier Customer Webhook URL"
                    defaultValue="https://hooks.zapier.com/hooks/catch/123456/abcdef/"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Zapier Appointment Webhook URL"
                    defaultValue="https://hooks.zapier.com/hooks/catch/123456/ghijkl/"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Zapier Assessment Webhook URL"
                    defaultValue="https://hooks.zapier.com/hooks/catch/123456/mnopqr/"
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormGroup>
                    <FormControlLabel 
                      control={<Switch defaultChecked />} 
                      label="Auto-send customers to CRM" 
                    />
                    <FormControlLabel 
                      control={<Switch defaultChecked />} 
                      label="Auto-send appointments to CRM" 
                    />
                    <FormControlLabel 
                      control={<Switch defaultChecked />} 
                      label="Auto-send assessments to CRM" 
                    />
                  </FormGroup>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Testing
                    </Typography>
                    <Button variant="outlined" size="small" sx={{ mr: 1 }} disabled>
                      Test Customer Webhook
                    </Button>
                    <Button variant="outlined" size="small" disabled>
                      Test Appointment Webhook
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom fontWeight="500">
                Google API Integration
              </Typography>
              <TextField
                fullWidth
                label="Google API Key"
                defaultValue="AIza*******************************************"
                margin="normal"
                type="password"
              />
              <TextField
                fullWidth
                label="Google Place ID"
                defaultValue="ChIJ***********************************"
                margin="normal"
              />
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          )}
          
          {tabValue === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="500">
                User Management
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Typography sx={{ mb: 2 }}>
                Manage admin users and permissions.
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                This feature is currently under development.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Settings;